import { ScenarioElement } from "../playerConfiguration/ScenarioElement";
import { Part } from "../playerConfiguration/Part";
import { PastOrCurrentPart } from "./PastOrCurrentPart";
import { PartDebugInfo } from "../playerConfiguration/DebugPartInfo";

export class ScenarioPath {
  private static FULL_LENGTH = 100;

  private _currentPart: PastOrCurrentPart;
  private _allElements: ScenarioElement[];

  private _pastParts: PastOrCurrentPart[];
  private _futureParts: Part[];
  private _allParts: Part[];
  private _partsDebugInfo: PartDebugInfo[];

  private constructor() {}

  get hasMultipleParts(): boolean {
    return this._allParts.length > 1;
  }

  get currentPartNumber(): number {
    return this._pastParts.length + 1;
  }

  get isLastPart(): boolean {
    return this._futureParts.length === 0;
  }

  get currentElementId(): string {
    return this._currentPart && this._currentPart.scenarioElement.id;
  }

  get currentElement(): ScenarioElement {
    return this._currentPart && this._currentPart.scenarioElement;
  }

  get allElements(): ScenarioElement[] {
    return this._allElements;
  }

  get displayedPastParts(): PastOrCurrentPart[] {
    return this._pastParts.filter((part) => part.size);
  }

  get displayedFutureParts(): Part[] {
    return this._futureParts.filter((part) => part.size);
  }

  get initialElement(): ScenarioElement {
    return this._allElements.find((el) => el.initial);
  }

  get currentPart(): PastOrCurrentPart {
    return this._currentPart;
  }

  static initialize(elements: ScenarioElement[], parts: Part[], partsDebugInfo?: PartDebugInfo[]): ScenarioPath {
    const scenarioPath = new ScenarioPath();
    scenarioPath._allElements = elements;
    scenarioPath._allParts = [...parts];
    scenarioPath._futureParts = [...parts];
    scenarioPath._pastParts = [];
    scenarioPath._partsDebugInfo = partsDebugInfo;
    return scenarioPath;
  }

  nowPlaying(scenarioElement: ScenarioElement) {
    if (this._currentPart && this._currentPart.scenarioElement.id === scenarioElement.id) {
      return this;
    }

    const copy = this.copy();

    if (!copy._allParts.length) {
      copy._currentPart = new PastOrCurrentPart(undefined, ScenarioPath.FULL_LENGTH, scenarioElement);
      return copy;
    }

    const newPartIndex = copy._allParts.findIndex((part) => part.id === scenarioElement.partId);
    const currentPartIndex = copy._currentPart && copy._allParts.findIndex((part) => part.id === copy._currentPart.id);

    if (copy._currentPart) {
      copy._pastParts[currentPartIndex] = copy._currentPart;
    }

    copy._futureParts = [...copy._allParts].slice(newPartIndex + 1);
    copy._pastParts = copy._pastParts.slice(0, newPartIndex);

    for (let idx = 0; idx < newPartIndex; idx++) {
      // need to add empty past parts if we skip some parts while jumping to scenario element
      if (!copy._pastParts[idx]) {
        const part = copy._allParts[idx];
        copy._pastParts[idx] = new PastOrCurrentPart(part.id, part.size, undefined);
      }
    }

    const newPart = copy._allParts[newPartIndex];
    copy._currentPart = new PastOrCurrentPart(newPart.id, newPart.size, scenarioElement);

    return copy;
  }

  private copy() {
    const copy = new ScenarioPath();
    copy._currentPart = this._currentPart;
    copy._allElements = this._allElements;

    copy._allParts = this._allParts;
    copy._pastParts = this._pastParts;
    copy._futureParts = this._futureParts;

    return copy;
  }
}
